
import { getBackgroundColor, getTextColor } from './imageTranslatorUtils';
import { TextBlock } from './imageTranslatorTypes.types';
import { api } from '../../utils/api';

export async function detectText(imageData: string) {
  try {
    const base64Image = imageData.split(',')[1];
    
    const response = await api.post(
      `/google/detect-text`,
      {
        requests: [{
          image: {
            content: base64Image
          },
          features: [{
            type: 'DOCUMENT_TEXT_DETECTION',
            maxResults: 50
          }]
        }]
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  
    const data = await response.json();
    const fullTextAnnotation = data.responses[0]?.fullTextAnnotation;
    
    if (!fullTextAnnotation) {
      return [];
    }

    const blocks = fullTextAnnotation.pages?.[0]?.blocks || [];
    const paragraphs = blocks.flatMap((block: any) => block.paragraphs || []);
    
    // Create image element for color detection
    const img = new Image();
    await new Promise((resolve) => {
      img.onload = resolve;
      img.src = imageData;
    });
    
    return paragraphs.map((paragraph: any, index: number) => {
      const words = paragraph.words || [];
      const text = words
        .map((word: any) => 
          word.symbols
            .map((symbol: any) => symbol.text)
            .join('')
        )
        .join(' ');

      const paragraphVertices = paragraph.boundingBox.vertices;
      const paragraphX = Math.min(...paragraphVertices.map((v: any) => v.x));
      const paragraphY = Math.min(...paragraphVertices.map((v: any) => v.y));
      const paragraphWidth = Math.max(...paragraphVertices.map((v: any) => v.x)) - paragraphX;
      const paragraphHeight = Math.max(...paragraphVertices.map((v: any) => v.y)) - paragraphY;

      // Check if the text is multiline by examining the first word's height
      // and checking if any word's Y position extends beyond the first line
      const firstWord = words[0];
      const firstWordVertices = firstWord.boundingBox.vertices;
      const firstWordHeight = Math.max(...firstWordVertices.map((v: any) => v.y)) - 
                            Math.min(...firstWordVertices.map((v: any) => v.y));
      
      const isMultiline = firstWordHeight < paragraphHeight * 0.7 || // If first word is significantly smaller than paragraph
                         words.some((word: any) => {
                           const wordY = Math.min(...word.boundingBox.vertices.map((v: any) => v.y));
                           return wordY > paragraphY + firstWordHeight; // If any word starts below the first line
                         });

      // Calculate font size based on word height for multiline text,
      // or paragraph height for single line text
      const fontSize = isMultiline
        ? Math.max(12, firstWordHeight)
        : Math.max(12, paragraphHeight);

      const x = paragraphX;
      const y = paragraphY;
      const width = paragraphWidth;
      const height = paragraphHeight;
      const block: TextBlock = {
        id: String(index),
        text,
        x,
        y,
        width,
        height,
        fontSize,
        fontFamily: 'Arial',
        fontWeight: '400',
        fontStyle: 'normal',
        rotation: 0,
        backgroundOpacity: 1.0,
        textOpacity: 1.0,
        backgroundColor: getBackgroundColor({ x, y, width, height }, img),
        color: getTextColor({ x, y, width, height }, img),
      };

      return block;
    });
  } catch (error) {
    console.error('Error detecting text:', error);
    throw error;
  }
}

// Translate text using Google Translate API
export async function translateText(
  texts: string[],
  sourceLanguage: string,
  targetLanguage: string
) {
  try {
    const response = await api.post(
      `/google/translate-text`,
      {
        q: texts,
        source: sourceLanguage,
        target: targetLanguage,
        format: 'text'
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    return data.data.translations.map((t: any) => t.translatedText);
  } catch (error) {
    console.error('Translation error:', error);
    throw error;
  }
}